import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  getRankingModel,
  validateRankingModel,
  moveRankingModelToTesting,
  previewRankingModelActivation,
  activateRankingModel,
} from "../../api/clientVendorDirectoryRankingApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import ClientVendorDirectoryRankingSubnav from "../../components/casamap/admin/ClientVendorDirectoryRankingSubnav";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatStatusLabel } from "../../utils/clientPortalFormat";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.ranking.modelDetail";
const STATUS_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.ranking.models.status";

export default function ClientVendorDirectoryRankingModelDetailPage() {
  const { clientViewId, modelId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t } = useLocale();
  const [detail, setDetail] = useState(null);
  const [activationPreview, setActivationPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const listPath = `${serviciosBase}/posicionamiento/modelos`;

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getRankingModel(clientViewId, modelId);
      setDetail(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, modelId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (action) => {
    setBusy(true);
    setMessage("");
    try {
      if (action === "validate") {
        const body = await validateRankingModel(clientViewId, modelId);
        setDetail((prev) => ({ ...prev, validation: body.validation }));
        setMessage(body.validation.valid ? t(`${COPY_PREFIX}.validationOk`) : t(`${COPY_PREFIX}.validationErrors`));
      } else if (action === "testing") {
        await moveRankingModelToTesting(clientViewId, modelId);
        setMessage(t(`${COPY_PREFIX}.movedToTesting`));
        await load();
      } else if (action === "activation-preview") {
        const body = await previewRankingModelActivation(clientViewId, modelId);
        setActivationPreview(body.preview);
      } else if (action === "activate") {
        const reason = window.prompt(t(`${COPY_PREFIX}.activateReason`));
        if (!reason) return;
        await activateRankingModel(clientViewId, modelId, { reason });
        setMessage(t(`${COPY_PREFIX}.activated`));
        await load();
      }
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setBusy(false);
    }
  };

  const model = detail?.model;
  const validation = detail?.validation;
  const editable = detail?.editable;

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <ClientVendorDirectoryRankingSubnav clientViewId={clientViewId} />
      <div className="cvd-admin__header">
        <div>
          <p className="cm-muted">
            <Link to={listPath}>{t(`${COPY_PREFIX}.modelsCrumb`)}</Link> / {model?.name || "…"}
          </p>
          <h1 className="cvd-admin__title">{model?.name || t(`${COPY_PREFIX}.fallbackTitle`)}</h1>
          <p className="lede">
            v{model?.version} · {t(`${STATUS_PREFIX}.${model?.status}`) || formatStatusLabel(model?.status)}
            {editable ? ` · ${t(`${COPY_PREFIX}.editable`)}` : ` · ${t(`${COPY_PREFIX}.readOnly`)}`}
          </p>
        </div>
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {message ? <div className="cvd-alert cvd-alert--success">{message}</div> : null}

      {!loading && model ? (
        <>
          <div className="cvd-risk-actions">
            <button type="button" className="btn" disabled={busy} onClick={() => runAction("validate")}>
              {t(`${COPY_PREFIX}.validate`)}
            </button>
            <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.RANKING_CONFIGURE}>
              {editable ? (
                <>
                  <button type="button" className="btn" disabled={busy} onClick={() => runAction("testing")}>
                    {t(`${COPY_PREFIX}.moveToTesting`)}
                  </button>
                  <button type="button" className="btn" disabled={busy} onClick={() => runAction("activation-preview")}>
                    {t(`${COPY_PREFIX}.activationPreview`)}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy}
                    onClick={() => runAction("activate")}
                  >
                    {t(`${COPY_PREFIX}.activate`)}
                  </button>
                </>
              ) : null}
            </VendorDirectoryPermissionGate>
          </div>

          {validation ? (
            <div className="cvd-panel">
              <h2>{t(`${COPY_PREFIX}.validationTitle`)}</h2>
              <p>
                {validation.valid
                  ? t(`${COPY_PREFIX}.noBlockingErrors`)
                  : t(`${COPY_PREFIX}.errorCount`, { count: validation.errors.length })}
              </p>
              {validation.warnings?.length ? (
                <ul>
                  {validation.warnings.map((warning, index) => (
                    <li key={index}>{warning.message}</li>
                  ))}
                </ul>
              ) : null}
              {validation.errors?.length ? (
                <ul>
                  {validation.errors.map((error, index) => (
                    <li key={index}>{error.message}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {activationPreview ? (
            <div className="cvd-panel">
              <h2>{t(`${COPY_PREFIX}.activationPreview`)}</h2>
              <p>
                {t(`${COPY_PREFIX}.canActivate`, {
                  value: activationPreview.canActivate ? t(`${COPY_PREFIX}.yes`) : t(`${COPY_PREFIX}.no`),
                })}
              </p>
              {activationPreview.blockers?.length ? (
                <p>{t(`${COPY_PREFIX}.blockers`, { blockers: activationPreview.blockers.join(", ") })}</p>
              ) : null}
              <p>{t(`${COPY_PREFIX}.strategy`, { strategy: activationPreview.rolloutStrategy })}</p>
            </div>
          ) : null}

          <div className="cvd-panel">
            <h2>{t(`${COPY_PREFIX}.configTitle`)}</h2>
            <pre className="cvd-code-block">{JSON.stringify(model.configSnapshot, null, 2)}</pre>
          </div>
        </>
      ) : null}
    </div>
  );
}
